import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { sportsMenuFullQuery } from '@/api/queries';
import { translateSportsLabel } from '@/i18n/sports-label';
import { resolveSport, toSlug } from '@/shell/sports-menu-utils';
import { EmptyState, useSportsFavorites } from '@/ui';
import { EventsFeed, EventsFeedSkeleton } from './-components/EventsFeed';

export const Route = createFileRoute('/sports/$sport/favorites')({
  component: FavoritesPage,
});

function FavoritesPage() {
  const { sport: sportSlug } = Route.useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const menuQuery = useQuery(sportsMenuFullQuery());
  const { prematchIds, hasPrematchFavorites } = useSportsFavorites();

  const sport = useMemo(
    () => resolveSport(menuQuery.data, sportSlug),
    [menuQuery.data, sportSlug],
  );

  const filter = useMemo(
    () => (sport && hasPrematchFavorites ? { Sport: sport.Name, EventIds: prematchIds } : null),
    [hasPrematchFavorites, prematchIds, sport],
  );

  if (menuQuery.isPending) {
    return <EventsFeedSkeleton />;
  }

  if (!sport) {
    return (
      <EmptyState
        icon="football"
        title={t('SportNotFound', { defaultValue: 'Sport not found' })}
        description={t('SportNotInMenu', {
          defaultValue: 'This sport is not in the current sports menu.',
        })}
      />
    );
  }

  if (!filter) {
    return (
      <EmptyState
        icon="star"
        title={t('Favourites', { defaultValue: 'Favourites' })}
        description={t('NoFavouriteEvents', {
          defaultValue: 'No favourite events yet. Tap the star on an event to add one.',
        })}
      />
    );
  }

  return (
    <EventsFeed
      scope="top-events"
      filter={filter}
      title={t('Favourites', { defaultValue: 'Favourites' })}
      subtitle={translateSportsLabel(i18n, t, sport.Id, sport.Name)}
      emptyDescription={t('NoFavouriteEventsForSport', {
        defaultValue: 'No favourite events available for this sport right now.',
      })}
      onOpenEvent={({ eventId, sport: eventSport, category, tournament, offer }) => {
        const resolvedSport = eventSport ? toSlug(eventSport) : sportSlug;
        const categorySlug = category ? toSlug(category) : undefined;
        const tournamentSlug = tournament ? toSlug(tournament) : undefined;
        if (!categorySlug || !tournamentSlug) return;
        void navigate({
          to: '/sports/$sport/$category/$tournament/$eventId',
          params: {
            sport: resolvedSport,
            category: categorySlug,
            tournament: tournamentSlug,
            eventId: String(eventId),
          },
          search: { offer },
        });
      }}
    />
  );
}
